import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import DocumentSignature from "components/CompanyDashboard/MaintainYourCompany/Changes/SharedComponents/DocumentSignature";
import DashboardWrapper from "components/lib/Global/DashboardWrapper";
import { getTransactionChangeByIdAsync } from "state/slices/initiateTransactionChange";
import Table from "components/lib/Global/ReactTable";
import { getFullName } from "utils";
import { format } from "date-fns";
import { getMaintainYourCompanyReviewStatus } from "config/documentData";
import { getComments } from "state/slices/comment";

const SignatureCorrectionPage = () => {
  const params = useParams();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);

  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(getTransactionChangeByIdAsync({ trxId: params.trxId }));
      setLoading(false);
    };
    fetchData();
  }, [dispatch, params.trxId]);

  const retrievedTransaction =
    initiatedTransactions?.transactionById?.retrievedTransactions || {};
  const data = [retrievedTransaction];

  const columns = useMemo(
    () => [
      {
        Header: () => "Full Name",
        id: "Name",
        accessor: "fullName",
        Cell: ({
          row: {
            original: { names },
          },
        }) => {
          return getFullName(names) || "N/A";
        },
      },
      {
        Header: "Change",
        id: "changeType",
        accessor: "changeType",
      },
      {
        Header: "TRX Ref",
        id: "trxRef",
        accessor: "trxRef",
      },
      {
        Header: "Date",
        Cell: ({ row }) => {
          const appointmentDate =
            row.original?.appointmentDate ||
            row.original?.official?.appointmentDate;
          return appointmentDate
            ? format(new Date(appointmentDate), "yyyy/MM/dd")
            : "N/A";
        },
      },
      {
        Header: "Status",
        id: "status",
        Cell: ({ row }) => {
          return getMaintainYourCompanyReviewStatus(
            row.original?.status.toString()
          );
        },
      },
      {
        Header: "Action",
        Cell: ({ row }) => {
          return (
            <ActionButtons
              row={row}
              toggleRowExpanded={row.toggleRowExpanded}
              isExpanded={row.isExpanded}
            />
          );
        },
      },
    ],
    []
  );

  // Automatically expand the first row after data is loaded
  useEffect(() => {
    if (!loading && data.length > 0) {
      const firstRow = data[0];
      if (firstRow && firstRow.toggleRowExpanded) {
        firstRow.toggleRowExpanded(true); // Automatically expand the first row
      }
    }
  }, [loading, data]);

  const renderRowSubComponent = React.useCallback(
    ({ row, rowProps, visibleColumns }) => {
      dispatch(getComments({ companyId: row.original.company._id }));
      return (
        <DocumentSignature
          row={row}
          rowProps={rowProps}
          visibleColumns={visibleColumns}
          currentTab={"stackHolderTab"}
          isViewMode={true}
        />
      );
    },
    []
  );

  if (loading) {
    return (
      <DashboardWrapper>
        <div className="w-full text-center">
          <p>Loading data...</p>
        </div>
      </DashboardWrapper>
    );
  }

  return (
    <DashboardWrapper>
      <div className="mt-8">
        <h3>
          {" "}
          Upload the required signature documents and click to submit for review
        </h3>
        <div className="w-full mt-2">
          <Table
            columns={columns}
            data={data}
            dataRowCellStyle="whitespace-normal"
            headingStyle={"bg-[#31415d] align-top"}
            tableColor="manage-table"
            renderRowSubComponent={renderRowSubComponent}
          />
        </div>
      </div>
    </DashboardWrapper>
  );
};

const ActionButtons = ({ row, toggleRowExpanded, isExpanded }) => {
  return (
    <button
      type="button"
      onClick={() => toggleRowExpanded()}
      className="text-sm text-gray-900"
    >
      {isExpanded ? "Close" : "Click to Upload"}
    </button>
  );
};

export default SignatureCorrectionPage;
